import { CircularProgress, LinearProgress, Stack, useTheme } from "@mui/material";
import { Outlet, useLocation } from "react-router";
import { Sidebar } from "./components/sidebar";
import { useEffect, useMemo } from "react";
import { ROUTES_URL } from "src/utils/constants";
import { GlobalDialog } from "./components/global-dialog";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useGetCustomerId } from "src/hooks";
import { hasValue } from "@itk/typescript-utils";
import { cacheCleanup } from "src/utils/cache-utils";
import { Shared_Languages, Shared_SystemSettings } from "src/backend/openapi/v1";
import { useTranslation } from "@itk/mui-components";
import { GlobalSearch } from "./components/global-search";
import { HeaderBreadcrumbs } from "./components/header-breadcrumbs";
import { applicationActions as applicationActions } from "src/components/application-store";
import { matchesRoute } from "src/utils";
import { FullScreenLoader } from "../full-screen-loader";

const mapStateToProps = (state: RootState) => ({
  contactPersonDetails: state.generalReducer.applicationReducer.contactPersonDetails,
  customers: state.generalReducer.applicationReducer.customers,
  globalLoadingBar: state.generalReducer.applicationReducer.globalLoadingBar,
  globalLoading: state.generalReducer.applicationReducer.globalLoading
});

const ownActions = {
  getContactPersonDetailsRequest: applicationActions.getContactPersonDetailsRequest,
  getPossibleCustomersRequest: applicationActions.getPossibleCustomersRequest,
  getCustomerRequestById: applicationActions.getCustomerRequestById,
  getSystemSettingsValueByIdRequest: applicationActions.getSystemSettingsValueByIdRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ApplicationWrapperBase = ({
  contactPersonDetails,
  getContactPersonDetailsRequest,
  getPossibleCustomersRequest,
  customers,
  getCustomerRequestById,
  getSystemSettingsValueByIdRequest,
  globalLoadingBar,
  globalLoading
}: Props) => {
  const location = useLocation();
  const theme = useTheme();
  const { customerId } = useGetCustomerId();
  const { i18n } = useTranslation();

  useEffect(() => {
    cacheCleanup();
  }, []);

  // Whether to add padding in particular pages or not
  const padding = useMemo(() => {
    const paths = [ROUTES_URL.PROJECTS, ROUTES_URL.CONFIGURATOR.build("0")];
    if (paths.some(x => matchesRoute(x, location.pathname))) {
      return {};
    } else {
      return { px: 5, py: 3 };
    }
  }, [location.pathname]);

  useEffect(() => {
    customers.loading === "not-started" && getPossibleCustomersRequest();
  }, [customers.loading]);

  useEffect(() => {
    if (hasValue(customerId)) {
      getContactPersonDetailsRequest(customerId);
      getCustomerRequestById(customerId);

      // get system settings
      getSystemSettingsValueByIdRequest(Shared_SystemSettings.DefaultTimeSpan);
    }
  }, [customerId]);

  // Language change as per contact person preference
  useEffect(() => {
    if (contactPersonDetails.loading === "has-loaded") {
      if (contactPersonDetails.data.languageId === Shared_Languages.English) i18n.changeLanguage("en");
      else if (contactPersonDetails.data.languageId === Shared_Languages.Danish) i18n.changeLanguage("da");
    }
  }, [contactPersonDetails]);

  const isFullScreenPage = useMemo(() => {
    const paths = [ROUTES_URL.CONFIGURE_PRODUCT.build("0", "0")];
    return paths.some(x => matchesRoute(x, location.pathname));
  }, [location.pathname]);

  if (contactPersonDetails.loading === "not-started") return <></>;
  if (contactPersonDetails.loading === "is-loading") {
    return (
      <Stack sx={{ height: "100%", width: "100%", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack direction="row" flex={1} overflow="hidden" sx={{ backgroundColor: theme.palette.customColors.lightGrey }}>
      {globalLoadingBar && <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: theme.zIndex.tooltip + 1 }} />}
      {globalLoading && <FullScreenLoader />}
      {isFullScreenPage ? (
        <Outlet />
      ) : (
        <>
          <Sidebar />

          <Stack flex={1} overflow="auto" direction="column">
            <Stack flex={1} overflow="auto" direction="column">
              <Stack
                gap={2}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                px={4}
                py={0.8}
                borderBottom={`1px solid ${theme.palette.customColors.grey}`}
                bgcolor={theme.palette.customColors.white}
              >
                <HeaderBreadcrumbs />
                <GlobalSearch />
              </Stack>
              <Stack flex={1} overflow="auto" {...padding}>
                <Outlet />
              </Stack>
            </Stack>
          </Stack>
        </>
      )}

      <GlobalDialog />
    </Stack>
  );
};

export const ApplicationWrapper = connect(mapStateToProps, ownActions)(ApplicationWrapperBase);
